import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { palette } from "../../styles/palette";
import { typography } from "../../styles/typography";

dayjs.locale("ko");

interface MonthSelectorProps {
    currentDate: dayjs.Dayjs;
    onDateChange: (newDate: dayjs.Dayjs) => void;
}

const MonthSelector: React.FC<MonthSelectorProps> = ({ currentDate, onDateChange }) => {
    // 날짜 선택 모드 (year: 연도 선택, month: 월 선택)
    const [dateSelectMode, setDateSelectMode] = useState<"none" | "year" | "month">("none");

    // 연도 목록 생성 (현재 연도 기준 +/-5년씩 총 20개)
    const currentYear = currentDate.year();
    const startYear = Math.floor(currentYear / 10) * 10 - 10; // 10년 단위로 처리
    const years = Array.from({ length: 20 }, (_, i) => startYear + i);

    // 월 목록
    const months = Array.from({ length: 12 }, (_, i) => i + 1);

    // 외부 클릭 감지를 위한 ref
    const dateSelectorRef = useRef<HTMLDivElement>(null);

    // 외부 클릭 시 선택기 닫기
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dateSelectorRef.current && !dateSelectorRef.current.contains(event.target as Node)) {
                setDateSelectMode("none");
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // 이전 달로 이동
    const handlePrevMonth = () => {
        onDateChange(currentDate.subtract(1, "month"));
    };

    // 다음 달로 이동
    const handleNextMonth = () => {
        onDateChange(currentDate.add(1, "month"));
    };

    // 날짜 선택 모드 토글
    const toggleDateSelector = () => {
        if (dateSelectMode === "none") {
            setDateSelectMode("month");
        } else {
            setDateSelectMode("none");
        }
    };

    // 연도 선택
    const selectYear = (year: number) => {
        onDateChange(currentDate.year(year));
        setDateSelectMode("month");
    };

    // 월 선택
    const selectMonth = (month: number) => {
        onDateChange(currentDate.month(month - 1));
        setDateSelectMode("none");
    };

    return (
        <CalendarHeaderContainer>
            <CalendarHeader>
                <ArrowIcon onClick={handlePrevMonth}>
                    <img src="/icon/calendarheader-left.png" alt="이전 달" width="24" height="24" />
                </ArrowIcon>
                <MonthLabel onClick={toggleDateSelector}>
                    {currentDate.format("YYYY년 M월")}
                    <DropdownIcon>▼</DropdownIcon>
                </MonthLabel>
                <ArrowIcon onClick={handleNextMonth}>
                    <img src="/icon/calendarheader-right.png" alt="다음 달" width="24" height="24" />
                </ArrowIcon>
            </CalendarHeader>

            {dateSelectMode !== "none" && (
                <DateSelectorContainer ref={dateSelectorRef}>
                    {dateSelectMode === "year" && (
                        <>
                            <DateSelectorTitle>
                                <BackButton onClick={() => setDateSelectMode("month")}>← 월 선택</BackButton>
                                <span>연도 선택</span>
                            </DateSelectorTitle>
                            <DateSelectorHeader>
                                <DateSelectorArrow
                                    onClick={() => {
                                        const newStartYear = startYear - 20;
                                        onDateChange(currentDate.year(newStartYear + 10));
                                    }}
                                >
                                    <img src="/icon/calendarheader-left.png" alt="이전 기간" width="20" height="20" />
                                </DateSelectorArrow>
                                <DateSelectorPeriod>
                                    {startYear} - {startYear + 19}
                                </DateSelectorPeriod>
                                <DateSelectorArrow
                                    onClick={() => {
                                        const newStartYear = startYear + 20;
                                        onDateChange(currentDate.year(newStartYear + 10));
                                    }}
                                >
                                    <img src="/icon/calendarheader-right.png" alt="다음 기간" width="20" height="20" />
                                </DateSelectorArrow>
                            </DateSelectorHeader>
                            <DateGrid>
                                {years.map((year) => (
                                    <DateItem
                                        key={year}
                                        onClick={() => selectYear(year)}
                                        isActive={year === currentDate.year()}
                                    >
                                        {year}
                                    </DateItem>
                                ))}
                            </DateGrid>
                        </>
                    )}

                    {dateSelectMode === "month" && (
                        <>
                            <DateSelectorTitle>
                                <span>{currentDate.year()}년 월 선택</span>
                                <BackButton onClick={() => setDateSelectMode("year")}>연도 선택 →</BackButton>
                            </DateSelectorTitle>
                            <DateGrid>
                                {months.map((month) => (
                                    <DateItem
                                        key={month}
                                        onClick={() => selectMonth(month)}
                                        isActive={month === currentDate.month() + 1}
                                    >
                                        {month}월
                                    </DateItem>
                                ))}
                            </DateGrid>
                        </>
                    )}
                </DateSelectorContainer>
            )}
        </CalendarHeaderContainer>
    );
};

const CalendarHeaderContainer = styled.div`
    position: relative;
    width: 100%;
`;

const CalendarHeader = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 4px 8px;
    gap: 8px;
    width: 100%;
    height: 32px;
`;

const ArrowIcon = styled.div`
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
        opacity: 0.7;
    }
`;

const MonthLabel = styled.div`
    ${typography.title3}
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: ${palette.gray[900]};
    cursor: pointer;
    user-select: none;

    &:hover {
        color: ${palette.primary};
    }
`;

const DropdownIcon = styled.span`
    font-size: 10px;
    margin-left: 5px;
    margin-top: 2px;
`;

const DateSelectorContainer = styled.div`
    position: absolute;
    top: 35px;
    left: 50%;
    transform: translateX(-50%);
    width: 280px;
    background: ${palette.white};
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 16px;
    z-index: 10;
`;

const DateSelectorTitle = styled.div`
    ${typography.body1}
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 700;
    color: ${palette.gray[900]};
    margin-bottom: 16px;
`;

const DateSelectorHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
`;

const DateSelectorArrow = styled.div`
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    &:hover {
        opacity: 0.7;
    }
`;

const DateSelectorPeriod = styled.div`
    ${typography.body1}
    font-weight: 700;
    color: ${palette.gray[800]};
`;

const BackButton = styled.button`
    background: none;
    border: none;
    ${typography.body2}
    color: ${palette.gray[600]};
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;

    &:hover {
        color: ${palette.primary};
    }
`;

const DateGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
`;

const DateItem = styled.div<{ isActive: boolean }>`
    padding: 8px 4px;
    ${typography.body1}
    text-align: center;
    color: ${(props) => (props.isActive ? palette.white : palette.gray[800])};
    cursor: pointer;
    border-radius: 4px;
    background: ${(props) => (props.isActive ? palette.primary : "transparent")};

    &:hover {
        background: ${(props) => (props.isActive ? palette.primary : palette.gray[100])};
    }
`;

export default MonthSelector;
